import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider } from '@mui/material/styles';
import { Suspense, useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';

import AssistantWidget from '@/components/layout/AssistantWidget';
import SideNav, {
  SHELL_BAR_HEIGHT,
  SIDENAV_WIDTH_COLLAPSED,
  SIDENAV_WIDTH_EXPANDED,
} from '@/components/layout/SideNav';
import TopBar from '@/components/layout/TopBar';
import { useAuth } from '@/contexts/AuthContext';
import { useThemeMode } from '@/contexts/ThemeContext';

const LS_KEY = 'invyo_sidenav_collapsed';

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === 'true';
  } catch {
    return false;
  }
}

export interface AppLayoutContext {
  setFullWidth: (v: boolean) => void;
  /** The SideNav's current width, for a page bar fixed beside it (New List's actions). */
  sidenavWidth: number;
}

/**
 * Authenticated layout shell: TopBar + SideNav + content area.
 *
 * One theme for the whole shell, and it follows the user's preference. There
 * used to be two providers nested: a permanently dark one around the chrome,
 * and the user's own around the content. So "light mode" produced a light page
 * framed by a dark nav and a dark bar, a defensible choice elsewhere (Slack
 * makes it), but nobody had chosen it here. It was the residue of the admin
 * theme swap, which needed a static shell to swap.
 *
 * /admin still differs by an amber accent in the chrome, not by a theme of its
 * own.
 */
export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [fullWidth, setFullWidth] = useState(false);
  const { contentTheme } = useThemeMode();

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_KEY, String(next));
      } catch {
        // localStorage unavailable: silently ignore
      }
      return next;
    });
  }, []);

  const sidenavWidth = collapsed ? SIDENAV_WIDTH_COLLAPSED : SIDENAV_WIDTH_EXPANDED;

  const { isAdmin } = useAuth();

  return (
    // Outermost, so the root's own `background.default` resolves from it too.
    // Nested one level in, that canvas read from App.tsx's always-dark theme,
    // which nothing revealed while `main` painted an opaque fill over it.
    <ThemeProvider theme={contentTheme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/*
          The wash the chrome is glass over. Fixed and behind everything, so both
          bars (which are themselves fixed, and translucent) have a gradient to
          bend rather than a flat fill to redraw at expense.

          Not the decorative layer that was removed: that one was a photo and a
          logo watermark at 55%, recognisable shapes crossing the cards. Nothing
          here is legible, and nothing here lands on top of content.
        */}
        <Box
          aria-hidden
          sx={(t) => ({
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: t.palette.surface.ambient.layer,
          })}
        />

        {/* The collapse toggle lives in the SideNav's own brand bar now. It was a
            fixed button pinned to `sidenavWidth - 14`, sitting on the seam it
            moves, and owned by the layout rather than by the nav it collapses. */}
        <SideNav collapsed={collapsed} isAdmin={isAdmin} onToggleCollapse={handleToggle} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            position: 'relative',
            // No fill of its own: the canvas belongs to the root Box, and an
            // opaque main would paint straight over the wash between them.
          }}
        >
          <TopBar sidenavWidth={sidenavWidth} />
          {/* Spacer: the AppBar is fixed, so without this the first row of every
              page starts underneath it. Content still slides under the bar once
              you scroll; that is the AppBar being fixed, not this being absent. */}
          <Box sx={{ height: SHELL_BAR_HEIGHT, flexShrink: 0 }} />

          <Box
            sx={{
              // Above the wash, which is `zIndex: 0` and fixed.
              position: 'relative',
              zIndex: 1,
              ...(!fullWidth && { maxWidth: { md: 1200, lg: 1400 } }),
              mx: 'auto',
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 2, sm: 3, md: 4 },
              color: 'text.primary',
            }}
          >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '60vh',
                    }}
                  >
                    <CircularProgress size={36} />
                  </Box>
                }
              >
              <Outlet context={{ setFullWidth, sidenavWidth } satisfies AppLayoutContext} />
            </Suspense>
          </Box>
        </Box>

        {/* AI Assistant: available on all authenticated pages. Inside the
            provider now, so it follows the mode like everything else does. */}
        <AssistantWidget />
      </Box>
    </ThemeProvider>
  );
}
